import { useEffect, useRef } from "react";

/**
 * Mounts the Telegram Login Widget script and forwards the auth payload
 * to a callback. Bot domain must be configured via BotFather (/setdomain)
 * to match the page origin, otherwise the widget renders an error.
 *
 * Docs: https://core.telegram.org/widgets/login
 */
type Props = {
  botUsername: string;
  onAuth: (payload: Record<string, unknown>) => void;
  size?: "small" | "medium" | "large";
  cornerRadius?: number;
  requestAccess?: boolean;
};

declare global {
  interface Window {
    onTelegramAuth?: (payload: Record<string, unknown>) => void;
  }
}

export function TelegramLoginButton({
  botUsername,
  onAuth,
  size = "large",
  cornerRadius = 12,
  requestAccess = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Global callback name needed by the widget's data-onauth attribute.
    window.onTelegramAuth = onAuth;

    const el = containerRef.current;
    if (!el) return;
    el.innerHTML = "";

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", size);
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-radius", String(cornerRadius));
    if (requestAccess) script.setAttribute("data-request-access", "write");
    el.appendChild(script);

    return () => {
      delete window.onTelegramAuth;
    };
  }, [botUsername, onAuth, size, cornerRadius, requestAccess]);

  return <div ref={containerRef} className="inline-block" />;
}
